'use client'

import { useSyncExternalStore } from 'react'

type NetworkInformationLike = EventTarget & {
  saveData?: boolean
}

type PreviewNavigator = Navigator & {
  connection?: NetworkInformationLike
  deviceMemory?: number
}

const LOW_MEMORY_DEVICE_GB = 4
const LOW_CONCURRENCY_DEVICE_CORES = 4

const subscribers = new Set<() => void>()
let stopObserving: (() => void) | null = null
let autoVideoPreviewsAllowed = false

const getServerSnapshot = () => false

const isSafari = (userAgent: string) =>
  /safari/i.test(userAgent) && !/(chrome|chromium|crios|fxios|android)/i.test(userAgent)

const isLowResourceDevice = (navigator: PreviewNavigator) => {
  const hasLowMemory = typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= LOW_MEMORY_DEVICE_GB
  const hasLowConcurrency =
    typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= LOW_CONCURRENCY_DEVICE_CORES

  return hasLowMemory || hasLowConcurrency
}

const calculateAutoVideoPreviewsAllowed = () => {
  const previewNavigator = navigator as PreviewNavigator
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const touchOnlyDevice = window.matchMedia('(hover: none) and (pointer: coarse)').matches
  const dataSaverEnabled = previewNavigator.connection?.saveData === true

  return !(
    isSafari(previewNavigator.userAgent) ||
    touchOnlyDevice ||
    reducedMotion ||
    dataSaverEnabled ||
    isLowResourceDevice(previewNavigator)
  )
}

const notifySubscribers = () => {
  const nextValue = calculateAutoVideoPreviewsAllowed()

  if (nextValue === autoVideoPreviewsAllowed) {
    return
  }

  autoVideoPreviewsAllowed = nextValue
  subscribers.forEach((subscriber) => subscriber())
}

const observeMediaQuery = (query: MediaQueryList, onChange: () => void) => {
  query.addEventListener('change', onChange)

  return () => {
    query.removeEventListener('change', onChange)
  }
}

const startObserving = () => {
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  const touchOnlyQuery = window.matchMedia('(hover: none) and (pointer: coarse)')
  const previewNavigator = navigator as PreviewNavigator
  const connection = previewNavigator.connection
  const cleanupMediaQueries = [
    observeMediaQuery(reducedMotionQuery, notifySubscribers),
    observeMediaQuery(touchOnlyQuery, notifySubscribers),
  ]

  connection?.addEventListener('change', notifySubscribers)
  notifySubscribers()

  return () => {
    cleanupMediaQueries.forEach((cleanup) => cleanup())
    connection?.removeEventListener('change', notifySubscribers)
  }
}

const subscribe = (subscriber: () => void) => {
  subscribers.add(subscriber)

  if (subscribers.size === 1) {
    stopObserving = startObserving()
  }

  return () => {
    subscribers.delete(subscriber)

    if (subscribers.size === 0) {
      stopObserving?.()
      stopObserving = null
      autoVideoPreviewsAllowed = false
    }
  }
}

/**
 * Enables automatic grid previews only when the browser and device are likely
 * to render several muted videos without harming scrolling performance.
 */
export default function useAutoVideoPreviewsAllowed() {
  return useSyncExternalStore(subscribe, () => autoVideoPreviewsAllowed, getServerSnapshot)
}
